import { currentLang, tr } from '../i18n/i18n.ts';
import { useSyncExternalStore } from 'react';
import { isPrepEntity, PREP_PROTOCOL_VERSION, type AppState, type DayTemplate, type ImportSource, type LocalEvent, type Overlay, type PlanCommitRequest, type PrepPlan, type SourceItem } from '@autoday/core';
import { api, ApiError } from '../api.ts';
import type { AuthStore } from '../auth/authStore.ts';
import type { AppStore } from '../store/appStore.ts';
import type { OutboxOp } from '../store/storage.ts';
import { platform, randomId } from '../platform.ts';
import type { PlanCommitBody, PlanCommitResponse, PlanResolveResponse, PullChange, PullResponse, PushOp, PushResult, SyncEntity } from './protocol.ts';
import { RemotePushConnector } from '../notifications/remotePush.ts';
import type { ServerConfig } from '../auth/authClient.ts';
import { blockMessage, staleMessage, type PlanCommitBlock, type PlanCommitOutcome, type PlanCommitReadiness } from '../prep/commit.ts';

export interface SyncConflict {
  entity: SyncEntity;
  id: string;
  title: string;
  localDoc: unknown;
  serverDoc: unknown;
  serverRevision: number;
  serverDeleted: boolean;
  at: string;
}

export interface SyncSnapshot {
  /**
   * local-only: gästprofil. paused: pausad (inför utloggning eller utan verifierad session ännu).
   * idle: senaste synk BEKRÄFTAD av servern. syncing / offline / error / auth-required (sessionen upphörd).
   */
  state: 'local-only' | 'paused' | 'idle' | 'syncing' | 'offline' | 'error' | 'auth-required';
  pending: number;
  conflicts: SyncConflict[];
  lastSyncAt: string | null;
  lastError: string | null;
}

type ResolveOutcome = PlanCommitOutcome | { ok: false; kind: 'not-applied'; message: string } | null;

/** Hur länge ett känt svar om serverns stöd för förberedelseplaner gäller innan det kontrolleras igen. */
const PREP_RECHECK_MS = 10 * 60_000;
/** Tidsgräns för ett godkännande (POST /api/prep/commit). */
const COMMIT_TIMEOUT_MS = 30_000;

/**
 * Lokal ändring sparas först (med utkorgspost i samma transaktion) och synkas sedan. Konflikter visas
 * – tyst last-write-wins förekommer inte för aktiviteter, överlägg eller källposter.
 * Synk körs bara i en kontoprofil, bara med verifierad session, och laddar aldrig upp gästdata.
 *
 * Förberedelseplaner (docs/PREPARATION-PLANS.md): planposter skickas bara till en server som bekräftat
 * stöd och hämtas i en egen ström med egen markör. Godkännanden går som en atomisk serveroperation
 * (commitPlan) – aldrig som lösa poster i utkorgen.
 */
export class SyncClient {
  private snap: SyncSnapshot = { state: 'local-only', pending: 0, conflicts: [], lastSyncAt: null, lastError: null };
  private listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private paused = false;
  private started = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private prepCheck: Promise<boolean | null> | null = null;
  private resolving: Promise<ResolveOutcome> | null = null;
  /** Godkännandet (opId) som just nu skickas från den här fliken – kontrolleras inte medan det är på väg. */
  private inflight: string | null = null;
  /** Godkännanden vars anrop från den här fliken är avslutat (med svar eller fel) – de kan kontrolleras direkt. */
  private finished = new Set<string>();

  constructor(
    private store: AppStore,
    private auth: AuthStore,
  ) {
    store.onCommitted.push(() => {
      if (!this.store.syncEnabled) return;
      void this.refreshPending();
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(() => void this.syncNow(), 1500);
    });
    auth.beforeSignOut.push(async () => {
      await this.pause();
    });
  }

  get snapshot() {
    return this.snap;
  }
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private emit(p: Partial<SyncSnapshot>) {
    const prevConflicts = this.snap.conflicts;
    this.snap = { ...this.snap, ...p };
    for (const l of this.listeners) l();
    if (p.conflicts && p.conflicts !== prevConflicts) void this.store.updateDevice((d) => ({ ...d, syncConflicts: p.conflicts! })).catch(() => undefined);
  }

  async start() {
    if (this.store.device.syncConflicts?.length) this.snap = { ...this.snap, conflicts: this.store.device.syncConflicts };
    if (!this.store.syncEnabled) {
      this.emit({ state: 'local-only' });
      return;
    }
    this.emit({ state: 'paused', lastSyncAt: this.store.device.lastSyncAt });
    await this.refreshPending();
    // planReadiness läser navigator.onLine – prenumeranter ritas om när nätet kommer och går.
    window.addEventListener('online', () => {
      this.emit({});
      void this.syncNow();
    });
    window.addEventListener('offline', () => this.emit({}));
    const onAuth = () => {
      const st = this.auth.state.status;
      if (st === 'signed-in') this.startTimers();
      if (st === 'expired') {
        this.stopTimers();
        this.emit({ state: 'auth-required' });
      }
      if (st === 'offline') this.emit({ state: 'offline' });
      // Inloggningsstatusen ingår i planReadiness.
      this.emit({});
    };
    this.auth.subscribe(onAuth);
    onAuth();
  }

  private startTimers() {
    if (this.started || this.paused) return;
    this.started = true;
    this.timer = setInterval(() => void this.syncNow(), 60_000);
    void this.syncNow();
  }

  private stopTimers() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.debounce) clearTimeout(this.debounce);
    this.started = false;
  }

  /** Pausar nya synkoperationer och väntar in en pågående (inför utloggning). */
  async pause(): Promise<void> {
    this.paused = true;
    this.stopTimers();
    await this.running?.catch(() => undefined);
    this.emit({ state: 'paused' });
  }

  /** Återuppta efter "Avbryt" i utloggningsdialogen – befintligt tillstånd behålls. */
  resume() {
    this.paused = false;
    const st = this.auth.state.status;
    if (st === 'signed-in') this.startTimers();
    else this.emit({ state: st === 'expired' ? 'auth-required' : 'offline' });
  }

  async refreshPending(): Promise<number> {
    const n = (await this.store.storageAdapter.outbox()).length;
    if (n !== this.snap.pending) this.emit({ pending: n });
    return n;
  }

  /** Ändringar som servern inte bekräftat: utkorg + olösta konflikter. */
  async unsyncedCount(): Promise<number> {
    return (await this.refreshPending()) + this.snap.conflicts.length;
  }

  /** "Synka först": kör en synk även när nya operationer är pausade. */
  async flush(): Promise<{ ok: boolean; pending: number; conflicts: number; error?: string }> {
    await this.syncNow(true);
    const outbox = await this.store.storageAdapter.outbox();
    const pending = await this.refreshPending();
    const conflicts = this.snap.conflicts.length;
    const ok = this.snap.state === 'idle' && pending === 0 && conflicts === 0;
    // Bara planposter kvar efter en lyckad synk: de väntar på en server med stöd för förberedelseplaner.
    const heldPrep = this.snap.state === 'idle' && pending > 0 && outbox.every((o) => o.kind || isPrepEntity(o.entity));
    if (this.paused) this.emit({ state: ok ? 'paused' : this.snap.state });
    return {
      ok,
      pending,
      conflicts,
      ...(ok
        ? {}
        : {
            error: conflicts
              ? tr('Det finns synkkonflikter att lösa först.', 'There are sync conflicts to resolve first.')
              : this.snap.state === 'offline'
                ? tr('Ingen kontakt med servern.', 'No contact with the server.')
                : heldPrep
                  ? blockMessage('server-unsupported')
                  : (this.snap.lastError ?? tr('Synken blev inte klar.', 'The sync did not finish.')),
          }),
    };
  }

  async syncNow(force = false): Promise<void> {
    if (!this.store.syncEnabled || (this.paused && !force)) return;
    const st = this.auth.state.status;
    if (st !== 'signed-in' && st !== 'offline') return;
    if (this.running) return this.running;
    if (!navigator.onLine) {
      this.emit({ state: 'offline', pending: (await this.store.storageAdapter.outbox()).length });
      return;
    }
    this.running = this.runOnce().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async runOnce(): Promise<void> {
    this.emit({ state: 'syncing' });
    try {
      await this.ensureDevice();
      // Stöd för förberedelseplaner avgörs före push: planposter skickas aldrig till en server utan stöd.
      // Frågan ställs bara när den behövs (planposter i utkorgen eller ett obekräftat godkännande); annars
      // avgör pull med prepSince stödet utan ett extra anrop.
      const pendingPrep = !!this.store.device.prepPendingCommit || (await this.store.storageAdapter.outbox()).some((o) => !!o.kind || isPrepEntity(o.entity));
      const prep = pendingPrep ? await this.checkPrepSupport() : this.store.device.serverPrep.supported === true;
      // Ett obekräftat godkännande kontrolleras med samma operations-ID (det godkänns aldrig på nytt).
      if (prep && this.store.device.prepPendingCommit) await this.resolvePlanCommit();
      await this.push();
      await this.pull();
      await this.ackLocalSchedule();
      // Fjärrpush som reserv (avstängd tills servern har FCM/APNs). Fel här stoppar aldrig synken.
      void this.remotePush()?.ensure();
      const now = new Date().toISOString();
      await this.store.updateDevice((d) => ({ ...d, lastSyncAt: now, lastSyncError: null }));
      this.emit({ state: 'idle', lastSyncAt: now, lastError: null, pending: (await this.store.storageAdapter.outbox()).length });
      if (this.auth.state.status === 'offline') void this.auth.verify();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const st = e instanceof ApiError && e.status === 401 ? 'auth-required' : e instanceof ApiError && (e.code === 'NETWORK' || e.code === 'TIMEOUT') ? 'offline' : 'error';
      await this.store.updateDevice((d) => ({ ...d, lastSyncError: msg }));
      this.emit({ state: st, lastError: msg, pending: (await this.store.storageAdapter.outbox()).length });
      // 401 = sessionen har verkligen upphört. ACCOUNT_MISMATCH = cookien tillhör ett annat konto.
      if (e instanceof ApiError && e.status === 401) {
        this.stopTimers();
        this.auth.markExpired();
      }
      if (e instanceof ApiError && e.code === 'ACCOUNT_MISMATCH') {
        this.stopTimers();
        void this.auth.handleAccountMismatch();
      }
    }
  }

  private async ensureDevice() {
    const dev = this.store.device;
    if (dev.deviceRegistered) return;
    await api(`/api/devices/${encodeURIComponent(dev.deviceId)}`, { method: 'PUT', body: { platform: platform(), name: navigator.userAgent.slice(0, 120) } });
    await this.store.updateDevice((d) => ({ ...d, deviceRegistered: true }));
  }

  private async push() {
    const outbox = await this.store.storageAdapter.outbox();
    if (!outbox.length) return;
    // Planposter skickas bara till en server som bekräftat stöd. Annars ligger de kvar i utkorgen och
    // räknas som väntande. Importbuntar (kind) slås aldrig ihop med vanliga poster och skickas separat,
    // efter dem.
    const prepOk = () => this.store.device.serverPrep.supported === true;
    const regular = outbox.filter((o) => !o.kind && (prepOk() || !isPrepEntity(o.entity)));
    // Slå ihop flera ändringar av samma post: skicka senaste dokumentet med äldsta basrevisionen.
    const byKey = new Map<string, { op: OutboxOp; seqs: number[] }>();
    for (const o of regular) {
      const k = `${o.entity}:${o.id}`;
      const cur = byKey.get(k);
      if (!cur) byKey.set(k, { op: o, seqs: [o.seq!] });
      else byKey.set(k, { op: { ...o, baseRevision: cur.op.baseRevision }, seqs: [...cur.seqs, o.seq!] });
    }
    const epoch = this.store.device.prepOpEpoch;
    const items = [...byKey.values()].map(({ op: o, seqs }): { op: PushOp; seqs: number[] } => ({
      seqs,
      op: {
        // Servern sparar en avvisning per opId: efter BAD_ENTITY (ny epok) får planposterna nya opId:n.
        opId: epoch && isPrepEntity(o.entity) ? `${o.opId}.${epoch}` : o.opId,
        entity: o.entity,
        id: o.id,
        baseRevision: this.serverRevision(o.entity, o.id),
        doc: o.doc,
        deleted: !!o.deleted || !!(o.doc as { deleted?: boolean } | null)?.deleted,
        ...(o.importId ? { importId: o.importId } : {}),
      },
    }));
    for (let i = 0; i < items.length; i += 200) {
      // Efter ett BAD_ENTITY tidigare i samma synk skickas inga fler planposter.
      const batch = items.slice(i, i + 200).filter((x) => prepOk() || !isPrepEntity(x.op.entity));
      if (!batch.length) continue;
      const slice = batch.map((x) => x.op);
      const res = await api<{ results: PushResult[] }>('/api/sync/push', { body: { deviceId: this.store.device.deviceId, ops: slice }, timeoutMs: 30000 });
      await this.handleResults(slice, res.results);
      // BAD_ENTITY för en planpost = servern saknar stöd (t.ex. efter en återgång): posten ligger kvar.
      const unsupported = new Set(res.results.filter((r) => prepUnsupported(slice, r)).map((r) => r.opId));
      if (unsupported.size) await this.setServerPrep(false, true);
      const handled = new Set(res.results.map((r) => r.opId).filter((id) => !unsupported.has(id)));
      await this.store.storageAdapter.removeOutbox(batch.filter((x) => handled.has(x.op.opId)).flatMap((x) => x.seqs));
    }
    if (prepOk()) await this.pushImports(outbox.filter((o) => o.kind === 'prepImport'));
  }

  /**
   * Gästöverförda planer: varje bunt (plan + dess pass) som EN atomisk import, samma serverväg som
   * godkännanden. Samma opId vid omförsök → servern svarar med det sparade utfallet (aldrig två uppsättningar).
   */
  private async pushImports(ops: OutboxOp[]) {
    for (const o of ops) {
      const bundle = o.doc as { plan: PrepPlan; events: LocalEvent[] };
      let res: PlanCommitResponse;
      try {
        res = await this.postCommit({ kind: 'import', opId: o.opId, plan: bundle.plan, events: bundle.events });
      } catch (e) {
        const known = commitResponseOf(e);
        if (known && known.status !== 'not-applied') res = known;
        else if (missingRoute(e)) {
          // Servern saknar stöd (t.ex. efter en återgång): bunten ligger kvar till en server med stöd.
          await this.setServerPrep(false);
          return;
        } else if (refused(e)) {
          // Servern avvisade själva bunten (formfel) – ett identiskt omförsök kan aldrig lyckas.
          await this.note(importNotice(this.store.state, bundle.plan, false));
          await this.store.storageAdapter.removeOutbox([o.seq!]);
          continue;
        } else throw e; // nätverks-, server- eller sessionsfel: ligger kvar och skickas igen med samma opId
      }
      if (res.status === 'applied' || res.status === 'duplicate') await this.applyChanges(res.docs);
      // Kontot hade redan en plan för deadlinen (eller underlaget gick inte att importera): ingenting skrevs.
      else await this.note(importNotice(this.store.state, bundle.plan, res.status === 'skipped'));
      await this.store.storageAdapter.removeOutbox([o.seq!]);
    }
  }

  private serverRevision(entity: SyncEntity, id: string): number | null {
    return this.knownServerRevisions()[`${entity}:${id}`] ?? null;
  }
  private knownServerRevisions(): Record<string, number> {
    return this.store.device.serverRevisions ?? {};
  }

  private async handleResults(ops: PushOp[], results: PushResult[]) {
    // Bara de egna uppdateringarna slås ihop med det som är sparat (en annan flik kan ha hunnit längre).
    const revs: Record<string, number> = {};
    const conflicts: SyncConflict[] = [...this.snap.conflicts];
    for (const r of results) {
      const op = ops.find((o) => o.opId === r.opId);
      if (!op) continue;
      const k = `${op.entity}:${op.id}`;
      if (r.status === 'applied' || r.status === 'duplicate' || r.status === 'converged') revs[k] = r.revision;
      if (r.status === 'conflict') {
        const existing = conflicts.findIndex((x) => x.entity === op.entity && x.id === op.id);
        if (existing >= 0) conflicts.splice(existing, 1);
        conflicts.push({ entity: op.entity, id: op.id, title: titleOf(op.entity, op.doc ?? r.serverDoc), localDoc: op.doc, serverDoc: r.serverDoc, serverRevision: r.serverRevision, serverDeleted: r.serverDeleted, at: new Date().toISOString() });
      }
      // En planpost som avvisas med BAD_ENTITY ligger kvar (servern saknar stöd) – inget fel att visa.
      if (r.status === 'rejected' && !prepUnsupported(ops, r)) {
        await this.store.updateDevice((d) => ({ ...d, observedErrors: [...d.observedErrors, { at: new Date().toISOString(), channel: 'sync', message: `${r.message} (${op.entity})` }].slice(-30) }));
      }
    }
    await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, ...revs } }));
    this.emit({ conflicts });
  }

  private async pull() {
    let cursor = this.store.device.syncCursor;
    // Planströmmen har en EGEN markör (prepSince). En server utan stöd svarar utan 'prep'.
    let prepCursor = this.store.device.prepCursor;
    // "Utan stöd" gäller en stund; därefter frågar pull igen (en äldre server ignorerar prepSince), så att en
    // uppgraderad server upptäcks utan ett separat anrop.
    const sp = this.store.device.serverPrep;
    let wantPrep = sp.supported !== false || sp.checkedAt === null || Date.now() - Date.parse(sp.checkedAt) >= PREP_RECHECK_MS;
    for (let guard = 0; guard < 50; guard++) {
      const res = await api<PullResponse>(`/api/sync/pull?since=${cursor}${wantPrep ? `&prepSince=${prepCursor}` : ''}`);
      const prep = wantPrep ? res.prep : undefined;
      if (wantPrep && !prep) {
        wantPrep = false;
        await this.setServerPrep(false);
      } else if (prep && this.store.device.serverPrep.supported !== true) await this.setServerPrep(true);
      // Vanliga ändringar och planändringar tillämpas tillsammans: samma skydd, en lagringstransaktion.
      const changes = [...res.changes, ...(prep?.changes ?? [])];
      if (changes.length) await this.applyChanges(changes);
      cursor = res.cursor;
      if (prep) prepCursor = prep.cursor;
      await this.store.updateDevice((d) => ({ ...d, syncCursor: Math.max(d.syncCursor, cursor), prepCursor: prep ? Math.max(d.prepCursor, prep.cursor) : d.prepCursor }));
      if (!res.more && !prep?.more) break;
    }
  }

  /**
   * Fjärrändringar – från pull eller ett serverbekräftat godkännande/en import. Osynkade lokala
   * ändringar OCH olösta konflikter skrivs aldrig över.
   */
  private async applyChanges(changes: Array<Pick<PullChange, 'entity' | 'id' | 'revision' | 'doc' | 'deleted'>>) {
    const seen = this.store.state;
    // Importbuntar (kind) finns inte i det lokala tillståndet och har inget att skydda.
    const outbox = (await this.store.storageAdapter.outbox()).filter((o) => !o.kind);
    const pendingKeys = new Set([...outbox.map((o) => `${o.entity}:${o.id}`), ...this.snap.conflicts.map((c) => `${c.entity}:${c.id}`)]);
    const revs: Record<string, number> = {};
    const conflicts: SyncConflict[] = [...this.snap.conflicts];
    await this.store.replace(
      (s) => {
        // Kan köras om på färskare data (en annan flik skrev emellan) – börja då från början.
        for (const k of Object.keys(revs)) delete revs[k];
        let next: AppState = s;
        const known = this.store.device.serverRevisions ?? {};
        for (const c of changes) {
          const k = `${c.entity}:${c.id}`;
          // Äldre än det enheten redan har från servern (t.ex. ett sent "duplicate"-svar med dokumenten som de
          // såg ut vid godkännandet, efter att planen ändrats på en annan enhet): backa aldrig lokala data.
          if (known[k] !== undefined && c.revision < known[k]!) continue;
          // Ändrad lokalt efter att utkorgen lästes (sparad medan svaret behandlades): räknas som
          // osynkad. Basrevisionen lämnas orörd, så att servern visar en konflikt vid nästa push.
          if (pendingKeys.has(k) || changedLocally(s, seen, c.entity, c.id)) {
            const idx = conflicts.findIndex((x) => x.entity === c.entity && x.id === c.id);
            if (idx >= 0) conflicts[idx] = { ...conflicts[idx]!, serverDoc: c.doc, serverRevision: c.revision, serverDeleted: c.deleted };
            continue;
          }
          next = applyRemote(next, c.entity, c.id, c.doc, c.deleted);
          revs[k] = c.revision;
        }
        return next;
      },
      { fromRemote: true },
    );
    await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, ...revs } }));
    this.emit({ conflicts });
  }

  private remote: RemotePushConnector | null = null;

  /** Native: registrera fjärrpush-token hos servern om servern stöder det. Webb: null. */
  private remotePush(): RemotePushConnector | null {
    const os = platform();
    if (os === 'web' || this.auth.profile.kind !== 'account') return null;
    const deviceId = this.store.device.deviceId;
    this.remote ??= new RemotePushConnector({
      os,
      accountTag: this.auth.profile.accountTag ?? 'unbound',
      serverFeatures: async () => (await api<ServerConfig>('/api/config', { timeoutMs: 8000 })).features.nativePush,
      putToken: (provider, token) => api(`/api/devices/${encodeURIComponent(deviceId)}/native-push`, { method: 'PUT', body: { provider, token } }).then(() => undefined),
      onOpened: (deliveryId) => {
        void api('/api/notifications/opened', { body: { deliveryId } }).catch(() => undefined);
        // Innehållet finns inte i fjärrpushen – det kommer med synken.
        window.location.hash = '#/idag';
        void this.syncNow(true);
      },
      onForeign: () => {
        window.location.hash = '#/idag';
      },
    });
    return this.remote;
  }

  /** Native: berätta för servern vilka larmpunkter enheten själv schemalagt → servern skickar inte dubbelt. */
  private async ackLocalSchedule() {
    if (platform() === 'web') return;
    const plan = this.store.device.lastPlan;
    if (!plan) return;
    await api(`/api/devices/${encodeURIComponent(this.store.device.deviceId)}/local-schedule`, {
      method: 'PUT',
      body: { pointIds: plan.scheduled.flatMap((n) => n.pointIds), coverageUntil: plan.coverageUntil, generatedAt: plan.generatedAt },
    });
  }

  // ---------------------------------------------------------------- förberedelseplaner (se prep/commit.ts)

  /**
   * Stöder servern förberedelseplaner (GET /api/config → features.prepPlans)? Svaret cachas i
   * enhetstillståndet och kontrolleras igen efter en stund. force = fråga servern nu. null = okänt
   * (servern kunde inte nås och inget tidigare svar gäller).
   */
  private checkPrepSupport(force = false): Promise<boolean | null> {
    const cur = this.store.device.serverPrep;
    const fresh = cur.checkedAt !== null && Date.now() - Date.parse(cur.checkedAt) < PREP_RECHECK_MS;
    if (!force && cur.supported !== null && fresh) return Promise.resolve(cur.supported);
    this.prepCheck ??= (async () => {
      try {
        const cfg = await api<{ features?: { prepPlans?: number } } | null>('/api/config', { timeoutMs: 8000 });
        const supported = (cfg?.features?.prepPlans ?? 0) >= PREP_PROTOCOL_VERSION;
        await this.setServerPrep(supported);
        return supported;
      } catch {
        // Ingen ny kunskap (nätverks- eller serverfel): senast kända svar gäller tills vidare.
        return force ? null : this.store.device.serverPrep.supported;
      }
    })().finally(() => {
      this.prepCheck = null;
    });
    return this.prepCheck;
  }

  /** rejected: servern avvisade planposter (BAD_ENTITY) – nästa försök behöver nya opId:n (se push). */
  private async setServerPrep(supported: boolean, rejected = false) {
    const checkedAt = new Date().toISOString();
    await this.store.updateDevice((d) => ({ ...d, serverPrep: { supported, checkedAt }, prepOpEpoch: rejected ? d.prepOpEpoch + 1 : d.prepOpEpoch }));
    this.emit({});
  }

  private postCommit(request: PlanCommitRequest): Promise<PlanCommitResponse> {
    const body: PlanCommitBody = { deviceId: this.store.device.deviceId, request };
    return api<PlanCommitResponse>('/api/prep/commit', { body, timeoutMs: COMMIT_TIMEOUT_MS });
  }

  /**
   * Atomiskt plangodkännande mot servern (POST /api/prep/commit). Skickas bara när kontot är inloggat,
   * enheten har kontakt, servern har stöd, inget tidigare godkännande väntar och synken är klar utan
   * konflikter. Ett förlorat svar kontrolleras med samma operations-ID – "inget skapades" sägs bara med belägg.
   */
  async commitPlan(req: PlanCommitRequest): Promise<PlanCommitOutcome> {
    const blocked = (block: PlanCommitBlock): PlanCommitOutcome => ({ ok: false, kind: 'blocked', block, message: blockMessage(block) });
    const st = this.auth.state.status;
    if (!this.store.syncEnabled || this.auth.profile.kind !== 'account' || st === 'guest' || st === 'expired') return blocked('signed-out');
    if (st === 'checking') return blocked('checking');
    if (!navigator.onLine) return blocked('offline');
    const supported = await this.checkPrepSupport(this.store.device.serverPrep.supported !== true);
    if (supported === null) return blocked('offline');
    if (!supported) return blocked('server-unsupported');
    // Ett obekräftat godkännande kontrolleras först med sitt operations-ID; inget nytt skickas förrän dess
    // utfall är känt. Gäller det samma begäran (samma opId) är det utfallet svaret – den skickas inte om.
    const earlier = this.store.device.prepPendingCommit;
    if (earlier) {
      const r = await this.resolvePlanCommit();
      if (this.store.device.prepPendingCommit) return blocked('pending-commit');
      if (r && earlier.opId === req.opId) return commitOutcome(r);
    }
    // Planen ska bygga på samma underlag som servern: synka först, utan väntande ändringar eller konflikter.
    // En synk som redan pågick kan ha läst utkorgen före användarens senaste ändring – då en gång till.
    await this.syncNow(true);
    if (this.snap.state === 'idle' && (await this.refreshPending()) > 0) await this.syncNow(true);
    if (this.snap.state === 'offline') return blocked('offline');
    if (this.snap.state === 'auth-required') return blocked('signed-out');
    if (this.snap.state !== 'idle') {
      return {
        ok: false,
        kind: 'blocked',
        block: 'sync-pending',
        message: tr('Synken blev inte klar, så planen kan inte jämföras med serverns underlag just nu. Inget har skickats – försök igen om en stund.', 'The sync did not finish, so the plan cannot be checked against the server’s data right now. Nothing has been sent – try again in a moment.'),
      };
    }
    if (this.store.device.serverPrep.supported !== true) return blocked('server-unsupported');
    if ((await this.refreshPending()) > 0) return blocked('sync-pending');
    if (this.snap.conflicts.length) return blocked('sync-conflicts');
    // Sparas INNAN anropet: ett förlorat svar kan alltid kontrolleras med samma operations-ID.
    const pending = {
      opId: req.opId,
      planId: req.kind === 'import' ? req.plan.id : req.planId,
      sourceKey: req.kind === 'import' ? req.plan.sourceKey : req.sourceKey,
      kind: req.kind,
      sentAt: new Date().toISOString(),
      checkedAt: null,
      request: req,
    };
    const saved = await this.store.updateDevice((d) => (d.prepPendingCommit && d.prepPendingCommit.opId !== req.opId ? d : { ...d, prepPendingCommit: pending }));
    // En annan flik hann skicka ett eget godkännande emellan.
    if (saved.prepPendingCommit?.opId !== req.opId) return blocked('pending-commit');
    this.emit({});
    let res: PlanCommitResponse | null = null;
    let failure: unknown = null;
    this.inflight = req.opId;
    try {
      res = await this.postCommit(req);
    } catch (e) {
      failure = e;
    } finally {
      this.inflight = null;
      this.finished.add(req.opId);
    }
    const known = res ?? commitResponseOf(failure);
    if (known && known.status !== 'not-applied') return this.finishCommit(req.opId, known);
    if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
      // Servern avvisade begäran innan den prövades (session, enhet, formkontroll) – inget skrevs och
      // inget sparades för operations-ID:t. Inte ett okänt utfall.
      await this.clearPending(req.opId);
      if (failure.status === 401) {
        this.stopTimers();
        this.auth.markExpired();
        return blocked('signed-out');
      }
      if (failure.code === 'ACCOUNT_MISMATCH') {
        this.stopTimers();
        void this.auth.handleAccountMismatch();
        return blocked('signed-out');
      }
      if (missingRoute(failure)) {
        await this.setServerPrep(false);
        return blocked('server-unsupported');
      }
      return { ok: false, kind: 'error', message: tr(`Servern tog inte emot godkännandet (${failure.message}). Inget lades in.`, `The server did not accept the approval (${failure.message}). Nothing was added.`) };
    }
    // Svaret kom inte fram (nätverk, tidsgräns, serverfel): utfallet är okänt. Kontrollera en gång med
    // samma operations-ID; går inte heller det är utfallet fortfarande okänt och godkännandet väntar kvar.
    const r = await this.resolvePlanCommit();
    return r ? commitOutcome(r) : { ok: false, kind: 'unknown', message: unknownMessage() };
  }

  /** Kontrollera ett obekräftat godkännande med samma operations-ID. null = inget väntar. */
  resolvePlanCommit(): Promise<ResolveOutcome> {
    this.resolving ??= this.resolveOnce().finally(() => {
      this.resolving = null;
    });
    return this.resolving;
  }

  private async resolveOnce(): Promise<ResolveOutcome> {
    const p = this.store.device.prepPendingCommit;
    if (!p) return null;
    const unknown: PlanCommitOutcome = { ok: false, kind: 'unknown', message: unknownMessage() };
    // Begäran kan fortfarande vara på väg – från den här fliken, eller från en annan flik (eller en sida som
    // laddats om) som skickade den för mindre än tidsgränsen sedan. En kontroll nu kunde förklara den
    // övergiven trots att den strax tillämpas; den kontrolleras därför först när den inte längre kan vara på väg.
    if (p.opId === this.inflight) return unknown;
    if (!this.finished.has(p.opId) && Date.now() - Date.parse(p.sentAt) < COMMIT_TIMEOUT_MS + 5000) return unknown;
    const st = this.auth.state.status;
    if (!navigator.onLine || (st !== 'signed-in' && st !== 'offline')) return unknown;
    let res: PlanResolveResponse;
    try {
      res = await api<PlanResolveResponse>('/api/prep/commit/resolve', { body: { deviceId: this.store.device.deviceId, opId: p.opId }, timeoutMs: 15000 });
    } catch (e) {
      const known = commitResponseOf(e);
      if (!known) {
        if (e instanceof ApiError && e.status === 401) this.auth.markExpired();
        if (missingRoute(e)) await this.setServerPrep(false);
        // Fortfarande okänt: det väntande godkännandet ligger kvar (senaste kontrollförsök noteras).
        const checkedAt = new Date().toISOString();
        await this.store.updateDevice((d) => (d.prepPendingCommit?.opId === p.opId ? { ...d, prepPendingCommit: { ...d.prepPendingCommit, checkedAt } } : d));
        return unknown;
      }
      res = known;
    }
    if (res.status === 'not-applied') {
      // Servern har markerat operationen som övergiven: den tillämpas aldrig senare.
      await this.clearPending(p.opId);
      return { ok: false, kind: 'not-applied', message: tr('Godkännandet nådde aldrig servern. Inget lades in.', 'The approval never reached the server. Nothing was added.') };
    }
    return this.finishCommit(p.opId, res);
  }

  /** Serverns utfall för ett godkännande (direkt eller via resolve). Det väntande godkännandet rensas. */
  private async finishCommit(opId: string, res: PlanCommitResponse): Promise<PlanCommitOutcome> {
    if (res.status === 'applied' || res.status === 'duplicate') {
      // Servern har planen oavsett: misslyckas den lokala sparningen hämtar nästa synk dokumenten.
      await this.applyChanges(res.docs).catch(() => undefined);
    }
    await this.clearPending(opId);
    switch (res.status) {
      case 'applied':
      case 'duplicate':
        return { ok: true, planId: res.planId, duplicate: res.status === 'duplicate', skipped: false };
      case 'skipped':
        return { ok: true, planId: res.planId, duplicate: false, skipped: true };
      case 'stale':
        return { ok: false, kind: 'stale', reason: res.reason, message: staleMessage(res.reason) };
      case 'abandoned':
        return {
          ok: false,
          kind: 'stale',
          reason: 'plan-changed',
          message: tr('Det här godkännandet kontrollerades tidigare och lades aldrig in. Granska förslaget och godkänn igen.', 'This approval was checked earlier and was never added. Review the suggestion and approve again.'),
        };
    }
  }

  private async clearPending(opId: string) {
    await this.store.updateDevice((d) => (d.prepPendingCommit?.opId === opId ? { ...d, prepPendingCommit: null } : d));
    this.emit({});
  }

  /** Beskedet sparas bland enhetens observerade fel (Påminnelsekoll) – gästens original ligger kvar. */
  private async note(message: string) {
    await this.store.updateDevice((d) => ({ ...d, observedErrors: [...d.observedErrors, { at: new Date().toISOString(), channel: 'sync', message }].slice(-30) }));
  }

  /** Varför ett kontogodkännande inte kan skickas just nu (null = det kan skickas). Samma ordning som commitPlan. */
  private planBlock(): PlanCommitBlock | null {
    const st = this.auth.state.status;
    if (this.auth.profile.kind !== 'account' || st === 'guest' || st === 'expired' || this.snap.state === 'auth-required') return 'signed-out';
    if (st === 'checking') return 'checking';
    if (!navigator.onLine || st === 'offline' || this.snap.state === 'offline') return 'offline';
    const supported = this.store.device.serverPrep.supported;
    if (supported === null) return 'checking';
    if (!supported) return 'server-unsupported';
    if (this.store.device.prepPendingCommit) return 'pending-commit';
    if (this.snap.pending > 0) return 'sync-pending';
    if (this.snap.conflicts.length) return 'sync-conflicts';
    return null;
  }

  private readinessSnap: PlanCommitReadiness = { ready: false, block: 'checking', pending: null, mode: 'account' };
  private readinessKey = '';
  /** Kan ett kontogodkännande skickas nu? Samma objekt tills något relevant ändrats (useSyncExternalStore). */
  planReadiness(): PlanCommitReadiness {
    const p = this.store.device.prepPendingCommit;
    const block = this.planBlock();
    const key = [block ?? 'ready', p?.opId ?? '', p?.checkedAt ?? '', currentLang()].join('|');
    if (key !== this.readinessKey) {
      this.readinessKey = key;
      const pending = p ? { opId: p.opId, planId: p.planId, sourceKey: p.sourceKey, kind: p.kind, sentAt: p.sentAt, checkedAt: p.checkedAt } : null;
      this.readinessSnap = block ? { ready: false, block, message: blockMessage(block), pending, mode: 'account' } : { ready: true, pending, mode: 'account' };
    }
    return this.readinessSnap;
  }

  /** Användarens beslut i en konflikt. */
  async resolveConflict(c: SyncConflict, choice: 'mine' | 'server') {
    const k = `${c.entity}:${c.id}`;
    if (choice === 'server') {
      await this.store.replace((s) => applyRemote(s, c.entity, c.id, c.serverDoc, c.serverDeleted), { fromRemote: true });
      await this.store.updateDevice((d) => ({ ...d, serverRevisions: { ...d.serverRevisions, [k]: c.serverRevision } }));
    } else {
      // En borttagen post (null eller tombstone) skickas som borttagning – samma form som i push.
      const deleted = c.localDoc === null || !!(c.localDoc as { deleted?: boolean } | null)?.deleted;
      const ops: PushOp[] = [{ opId: randomId('op_'), entity: c.entity, id: c.id, baseRevision: c.serverRevision, doc: c.localDoc, deleted }];
      const res = await api<{ results: PushResult[] }>('/api/sync/push', { body: { deviceId: this.store.device.deviceId, ops } });
      await this.handleResults(ops, res.results);
      // En planpost som servern inte längre kan ta emot (t.ex. efter en återgång): konflikten ligger kvar.
      if (res.results.some((r) => prepUnsupported(ops, r))) {
        await this.setServerPrep(false);
        throw new Error(blockMessage('server-unsupported'));
      }
    }
    this.emit({ conflicts: this.snap.conflicts.filter((x) => x !== c) });
  }
}

/** BAD_ENTITY för en planpost: servern saknar stöd för förberedelseplaner. */
function prepUnsupported(ops: PushOp[], r: PushResult): boolean {
  return r.status === 'rejected' && r.code === 'BAD_ENTITY' && isPrepEntity(ops.find((o) => o.opId === r.opId)?.entity ?? '');
}

/** 404 utan AutoDays egen felkod: vägen finns inte – servern saknar stöd för förberedelseplaner. */
function missingRoute(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && e.code !== 'NOT_FOUND';
}

/**
 * Servern avvisade själva begäran (formkontroll, för stor) – inget skrevs och ett identiskt omförsök kan
 * inte lyckas. Sessions- och kontofel (401, 409 ACCOUNT_MISMATCH), okänd enhet och begränsningar är det inte.
 */
function refused(e: unknown): boolean {
  return e instanceof ApiError && [400, 413, 422].includes(e.status);
}

const COMMIT_STATUSES = new Set(['applied', 'duplicate', 'skipped', 'stale', 'abandoned', 'not-applied']);
/** Utfallet av ett godkännande även när servern skickar det med en felstatus (t.ex. 409 för stale). */
function commitResponseOf(e: unknown): PlanResolveResponse | null {
  const b = e instanceof ApiError ? (e.body as { status?: unknown } | null | undefined) : null;
  return b && typeof b.status === 'string' && COMMIT_STATUSES.has(b.status) ? (b as PlanResolveResponse) : null;
}

/**
 * Kontrollens (resolve) utfall som godkännandets utfall. "Inte tillämpat" betyder att ingenting skrevs och
 * att operations-ID:t aldrig kan tillämpas – precis som vid ändrat underlag behövs ett nytt förslag.
 */
function commitOutcome(r: NonNullable<ResolveOutcome>): PlanCommitOutcome {
  if (r.ok || r.kind !== 'not-applied') return r;
  return { ok: false, kind: 'stale', reason: 'plan-changed', message: r.message };
}

function unknownMessage(): string {
  return tr(
    'Godkännandet skickades men svaret från servern kom inte fram. AutoDay kontrollerar utfallet med servern och visar det när det är känt.',
    'The approval was sent but the server’s answer did not arrive. AutoDay checks the outcome with the server and shows it once it is known.',
  );
}

function importNotice(s: AppState, plan: PrepPlan, exists: boolean): string {
  const name = s.sourceItems[plan.sourceKey]?.data.title || plan.snapshot?.name || plan.sourceKey;
  return exists
    ? tr(`Förberedelseplanen för "${name}" fanns redan i kontot och lades inte till igen. Originalet finns kvar i gästläget.`, `The preparation plan for "${name}" was already in the account and was not added again. The original remains in guest mode.`)
    : tr(`Förberedelseplanen för "${name}" kunde inte läggas till i kontot. Originalet finns kvar i gästläget.`, `The preparation plan for "${name}" could not be added to the account. The original remains in guest mode.`);
}

/** Har posten ändrats lokalt mellan två tillstånd? (Objektidentitet – domänen ändrar alltid oföränderligt.) */
function changedLocally(a: AppState, b: AppState, entity: SyncEntity, id: string): boolean {
  switch (entity) {
    case 'event':
      return a.events[id] !== b.events[id];
    case 'overlay':
      return a.overlays[id] !== b.overlays[id];
    case 'template':
      return a.templates[id] !== b.templates[id];
    case 'sourceItem':
      return a.sourceItems[id] !== b.sourceItems[id];
    case 'importSource':
      return a.sources[id] !== b.sources[id];
    case 'meta':
      return a.settings !== b.settings || a.calendars !== b.calendars || a.categories !== b.categories;
    case 'prepTemplate':
      return a.prepTemplates?.[id] !== b.prepTemplates?.[id];
    case 'prepRule':
      return a.prepRules?.[id] !== b.prepRules?.[id];
    case 'prepPlan':
      return a.prepPlans?.[id] !== b.prepPlans?.[id];
    default:
      // Okänd posttyp (nyare server): räknas som lokalt ändrad så att den aldrig skrivs över eller tillämpas.
      return true;
  }
}

function titleOf(entity: SyncEntity, doc: unknown): string {
  if (entity === 'meta') return tr('Inställningar och kalendrar', 'Settings and calendars');
  if (entity === 'prepPlan') return tr('Förberedelseplan', 'Preparation plan');
  if (entity === 'prepRule') return tr('Regel för automatiska förslag', 'Rule for automatic suggestions');
  // Mallens namn är användarens egen text.
  if (entity === 'prepTemplate') return (doc as { name?: string } | null)?.name || tr('Förberedelsemall', 'Preparation template');
  const d = doc as { title?: string; data?: { summary?: string }; key?: string; name?: string } | null;
  return d?.title ?? d?.data?.summary ?? d?.name ?? d?.key ?? tr('Post', 'Item');
}

export function applyRemote(s: AppState, entity: SyncEntity, id: string, doc: unknown, deleted: boolean): AppState {
  switch (entity) {
    case 'event':
      return { ...s, events: { ...s.events, [id]: { ...(doc as LocalEvent), ...(deleted ? { deleted: true } : {}) } } };
    case 'overlay': {
      const o = { ...s.overlays };
      if (deleted) delete o[id];
      else o[id] = doc as Overlay;
      return { ...s, overlays: o };
    }
    case 'template': {
      const t = { ...s.templates };
      if (deleted) delete t[id];
      else t[id] = doc as DayTemplate;
      return { ...s, templates: t };
    }
    case 'sourceItem': {
      const si = { ...s.sourceItems };
      if (deleted) delete si[id];
      else si[id] = doc as SourceItem;
      return { ...s, sourceItems: si };
    }
    case 'importSource':
      return { ...s, sources: { ...s.sources, [id]: doc as ImportSource } };
    case 'meta': {
      const m = doc as Pick<AppState, 'settings' | 'calendars' | 'categories'>;
      return { ...s, settings: m.settings ?? s.settings, calendars: m.calendars ?? s.calendars, categories: m.categories ?? s.categories };
    }
    case 'prepTemplate':
    case 'prepRule':
    case 'prepPlan': {
      const key = entity === 'prepTemplate' ? 'prepTemplates' : entity === 'prepRule' ? 'prepRules' : 'prepPlans';
      const coll: Record<string, unknown> = { ...(s[key] ?? {}) };
      if (deleted || doc === null || doc === undefined) delete coll[id];
      else coll[id] = doc;
      return { ...s, [key]: coll } as AppState;
    }
    default:
      // Okänd posttyp från en nyare server: lämna tillståndet orört (förstör aldrig lokal data).
      return s;
  }
}

let client: SyncClient | null = null;
export function setSyncClient(c: SyncClient) {
  client = c;
}
export function getSyncClient(): SyncClient {
  if (!client) throw new Error('Synk ej initierad');
  return client;
}
export function useSync(): SyncSnapshot {
  const c = getSyncClient();
  return useSyncExternalStore(c.subscribe, () => c.snapshot);
}
